import React, { createContext, useContext, useState } from 'react';
import { useAnalytics } from './AnalyticsContext';

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Welcome to Halifax Audi! I'm your virtual assistant, here to help you discover the perfect Audi for Nova Scotia roads. I can assist with information about our quattro AWD models, Audi technology, financing options, service appointments, and even send you vehicle details via text. How can I help you experience Vorsprung durch Technik today?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const { trackEvent } = useAnalytics();

  const sendMessage = async (text, userPhone = null) => {
    const userMessage = {
      id: messages.length + 1,
      text,
      sender: 'user',
      timestamp: new Date(),
      phone: userPhone
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Track chat usage
    trackEvent('chat_message_sent', { message_length: text.length });

    try {
      const response = await getAudiAIResponse(text, userPhone);
      const botMessage = {
        id: messages.length + 2,
        text: response,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error getting AI response:', error);
      const errorMessage = {
        id: messages.length + 2,
        text: "I apologize, I'm having trouble processing your request right now. Please try again or call us directly at (902) 455-7890 to speak with our Audi Brand Specialists.",
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const getAudiAIResponse = async (userMessage, userPhone) => {
    // Enhanced Audi-focused AI responses
    const message = userMessage.toLowerCase();

    // Audi-specific model inquiries
    if (message.includes('q3') || message.includes('compact suv')) {
      return "The Audi Q3 is perfect for Halifax! This compact luxury SUV features quattro AWD for our Maritime winters, a 2.0L TFSI engine with 228 hp, and Audi Virtual Cockpit technology. Starting at $42,800, it includes MMI Navigation Plus and Bang & Olufsen sound. The Q3's progressive design and advanced safety features make it ideal for both city driving and weekend adventures. Would you like me to send you detailed specs and available colors via text?";
    }

    if (message.includes('q5') || message.includes('mid-size suv')) {
      return "Excellent choice! The Audi Q5 is our most popular SUV in Halifax. It perfectly balances luxury and capability with quattro AWD, a 261 hp 2.0L TFSI engine, and Audi Virtual Cockpit Plus. Starting at $53,900, it features Bang & Olufsen 3D sound and Matrix LED headlights. The Q5's sophisticated design and advanced driver assistance make it perfect for Nova Scotia roads. I can send you photos and current incentives via text - what's your number?";
    }

    if (message.includes('e-tron') || message.includes('electric') || message.includes('ev')) {
      return "The Audi e-tron GT represents the future of luxury performance! This stunning electric gran turismo delivers 469 hp with quattro electric AWD and up to 238 miles of range. With 800-volt architecture, you can charge from 10-80% in just 22.5 minutes. Starting at $107,300, it features Audi Virtual Cockpit Plus and Bang & Olufsen Premium Sound. Perfect for eco-conscious Halifax drivers who won't compromise on performance. Interested in a test drive?";
    }

    if (message.includes('rs') || message.includes('performance') || message.includes('fast')) {
      return "You're looking at Audi Sport territory! Our RS 6 Avant is the ultimate performance wagon - 591 hp from a 4.0L twin-turbo V8, 0-60 mph in 3.5 seconds, yet practical enough for daily use. With quattro AWD and RS sport suspension, it handles Halifax winters and Maritime roads with confidence. Starting at $119,500, it's the perfect blend of supercar performance and everyday versatility. Would you like to schedule a thrilling test drive?";
    }

    if (message.includes('quattro') || message.includes('awd') || message.includes('winter')) {
      return "quattro is Audi's legendary all-wheel drive system - perfect for Halifax winters! Unlike basic AWD systems, quattro provides superior traction and handling in all conditions. Our quattro models intelligently distribute power between wheels for optimal grip on Nova Scotia's varied road conditions. Every Audi except the A3 comes standard with quattro. It's not just about winter - quattro enhances performance year-round. Which Audi model interests you most?";
    }

    if (message.includes('virtual cockpit') || message.includes('technology') || message.includes('mmi')) {
      return "Audi's technology is truly advanced! The Audi Virtual Cockpit replaces traditional gauges with a customizable 12.3\" high-resolution display. MMI (Multi Media Interface) provides intuitive control of navigation, entertainment, and vehicle settings. Features like MMI Touch Response with dual screens, wireless Apple CarPlay®, and Audi Connect keep you connected. Our Bang & Olufsen sound systems deliver concert-hall acoustics. Which technology features are most important to you?";
    }

    // Halifax-specific vehicle recommendations
    if (message.includes('halifax') || message.includes('nova scotia') || message.includes('maritime')) {
      return "Living in Halifax, you need a vehicle that handles our unique Maritime conditions! I recommend Audi models with quattro AWD for winter confidence, heated seats and steering wheel for comfort, and rust protection for our salty roads. Popular Halifax choices include the Q5 for families, A4 for commuters, and Q3 for urban adventures. All feature Audi's advanced safety technology. What type of driving do you do most around Halifax?";
    }

    // Audi Care and Service
    if (message.includes('service') || message.includes('maintenance') || message.includes('audi care')) {
      return "Halifax Audi's service center offers exceptional care for your Audi! Our factory-trained technicians use genuine Audi parts and advanced diagnostic equipment. We offer Audi Care prepaid maintenance plans to lock in today's prices, plus convenient scheduling and premium customer lounges. Services include everything from oil changes to complex repairs, plus winter tire storage. Our service hours are Mon-Fri 7:30AM-5:30PM, Sat 8AM-4PM. Need to schedule service?";
    }

    // Financing with Audi focus
    if (message.includes('financing') || message.includes('lease') || message.includes('payment')) {
      return "Halifax Audi offers competitive financing and attractive lease options! Current promotions include low APR financing on select models and flexible lease terms. Audi Financial Services provides specialized programs for Audi owners, including loyalty incentives and graduate programs. We work with various credit situations and offer protection products. Our finance team understands Audi ownership benefits. Would you like me to send you current rates and payment calculators via text?";
    }

    // Test drive with Audi emphasis
    if (message.includes('test drive') || message.includes('drive') || message.includes('experience')) {
      if (userPhone) {
        trackEvent('test_drive_request', { phone: userPhone });
        return `Perfect! I'll arrange your Audi test drive at Halifax Audi. I'll send you a text at ${userPhone} with available times and preparation details. Our Audi Brand Specialists will have your chosen model ready, and you'll experience features like quattro AWD, Audi Virtual Cockpit, and our refined performance. Which Audi model would you like to test drive on Halifax roads?`;
      } else {
        return "Experiencing an Audi is the best way to understand what makes it special! Our test drives let you feel quattro AWD, experience Audi Virtual Cockpit, and enjoy our refined performance and luxury. I can schedule your test drive and send you confirmation details via text. What's your phone number, and which Audi model interests you most?";
      }
    }

    // Audi brand heritage and philosophy
    if (message.includes('audi') || message.includes('brand') || message.includes('vorsprung')) {
      return "Audi represents 'Vorsprung durch Technik' - progress through technology. Since 1969, we've pioneered innovations like quattro AWD, Audi Virtual Cockpit, and now electric e-tron models. Our four rings symbolize the union of four companies that created the modern Audi. Every Audi combines progressive design, advanced technology, and sporty performance. At Halifax Audi, we're proud to represent this heritage of innovation. What aspect of Audi interests you most?";
    }

    // Inventory and availability
    if (message.includes('inventory') || message.includes('available') || message.includes('stock')) {
      return "Halifax Audi maintains an excellent selection of new Audi models! We currently have Q3, Q5, Q7, A4, A6, and select performance models in stock. We also offer Audi Certified Pre-Owned vehicles with comprehensive warranties. Our inventory includes various colors, packages, and options. I can check specific availability and send you photos of available vehicles via text. What Audi model are you shopping for?";
    }

    // Hours and location
    if (message.includes('hours') || message.includes('open') || message.includes('location') || message.includes('columbus')) {
      return "Halifax Audi is located at 6077 Columbus St, Halifax, NS B3K 0E9. Our showroom hours are Mon-Thu 9AM-8PM, Fri-Sat 9AM-6PM, Sunday Closed. Our service department is Mon-Fri 7:30AM-5:30PM, Sat 8AM-4PM. We're easily accessible with ample parking and a comfortable showroom featuring the latest Audi models. I can send you directions and contact information via text if you'd like!";
    }

    // Default Audi-focused response
    if (message.includes('hello') || message.includes('hi') || message.includes('hey') || message.includes('good')) {
      return "Hello! Welcome to Halifax Audi, where we're passionate about Vorsprung durch Technik - progress through technology. I'm here to help you discover the perfect Audi for Nova Scotia roads. Whether you're interested in our quattro AWD models, advanced Audi technology, or want to schedule a test drive, I'm here to assist. I can also send detailed information via text for your convenience. What brings you to Audi today?";
    }

    // Comprehensive default response
    return "Thank you for your interest in Audi! At Halifax Audi, we're proud to offer the complete Audi experience:\n\n• Audi Q3, Q5, Q7 SUVs with quattro AWD\n• A4, A6 luxury sedans with advanced technology\n• e-tron electric models for sustainable luxury\n• RS performance models for ultimate driving\n• Audi Certified Pre-Owned vehicles\n• Factory-trained service and genuine parts\n• Competitive financing and Audi Financial Services\n\nOur Audi Brand Specialists understand what makes each model special and can help you find your perfect match. I can send you detailed information via text, or feel free to ask about specific models, technology, or services. What aspect of Audi ownership interests you most?";
  };

  const clearChat = () => {
    setMessages([
      {
        id: 1,
        text: "Welcome to Halifax Audi! I'm your virtual assistant, here to help you discover the perfect Audi for Nova Scotia roads. I can assist with information about our quattro AWD models, Audi technology, financing options, service appointments, and even send you vehicle details via text. How can I help you experience Vorsprung durch Technik today?",
        sender: 'bot',
        timestamp: new Date()
      }
    ]);
  };

  const value = {
    isOpen,
    setIsOpen,
    messages,
    sendMessage,
    clearChat,
    isLoading
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};